import { useQuery } from "@apollo/client";
import { create, show, useModal } from "@ebay/nice-modal-react";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { ChevronDown } from "components/icons/generated";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ModalSimple from "ds/components/ModalSimple";
import ModalHeader from "ds/components/Modal/Header";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import useTypedContext from "hooks/useTypedContext";
import { TermsAndConditionsVersion } from "types/generated";
import { ThemeContext } from "views/Theme";

import { GET_AI_SETTINGS } from "./gql";
import styles from "./styles.module.css";

const AcceptToSModal = create(function AcceptToSModal() {
  const modal = useModal();
  const { isDarkMode } = useTypedContext(ThemeContext);
  const ref = useRef<HTMLDivElement>(null);
  const [showScrollForMore, setShowScrollForMore] = useState(true);

  const { data } = useQuery<{
    spaceliftIntelligenceTermsLatestVersion: TermsAndConditionsVersion;
  }>(GET_AI_SETTINGS, { fetchPolicy: "cache-and-network" });

  const content = data?.spaceliftIntelligenceTermsLatestVersion.content;

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const handleScroll = () => {
      setShowScrollForMore(el.scrollTop === 0);
    };

    el.addEventListener("scroll", handleScroll);

    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollToBottom = () =>
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });

  return (
    <ModalSimple size="large">
      <ModalHeader title="Spacelift Intelligence Terms of Use" />
      <ModalContent padding="0">
        <Box ref={ref} scrollable className={styles.wrapper}>
          <Markdown className={styles.legalText} markup={content ?? ""} openLinksInNewTab />
          <Box
            className={cx(styles.scrollForMore, {
              [styles.show]: showScrollForMore,
              [styles.darkMode]: isDarkMode,
            })}
            justify="center"
            align="center"
            fullWidth
          >
            <Button variant="secondary" onPress={handleScrollToBottom} startIcon={ChevronDown}>
              Scroll to read more
            </Button>
          </Box>
        </Box>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onPress={modal.hide}>
          Close
        </Button>
      </ModalFooter>
    </ModalSimple>
  );
});

export const showAcceptToSModal = () => show(AcceptToSModal);
